/**
 * Inquiry Modal Module
 *
 * Handles the property inquiry modal with form submission.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';

export default class InquiryModal {
  constructor() {
    this.modal = document.querySelector('[data-inquiry-modal]');
    this.triggers = document.querySelectorAll('[data-inquiry-open]');
    this.closeButtons = document.querySelectorAll('[data-inquiry-close]');
    this.form = document.querySelector('[data-inquiry-form]');

    if (!this.modal) return;

    this.isOpen = false;
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Open triggers
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close buttons
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Close on backdrop click
    const backdrop = this.modal.querySelector('[data-inquiry-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close());
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Prevent wheel events from reaching Lenis when modal is open
    this.modal.addEventListener(
      'wheel',
      (e) => {
        if (this.isOpen) {
          e.stopPropagation();
        }
      },
      { passive: false }
    );

    // Also block touch move from propagating
    this.modal.addEventListener(
      'touchmove',
      (e) => {
        if (this.isOpen) {
          e.stopPropagation();
        }
      },
      { passive: false }
    );

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    // Stop Lenis smooth scroll to prevent scroll bleed-through
    const lenis = window.TRE?.getLenis?.();
    if (lenis) {
      lenis.stop();
    }

    // Animate in
    gsap.to(this.modal, {
      opacity: 1,
      visibility: 'visible',
      duration: 0.3,
      ease: 'power2.out',
      onStart: () => {
        this.modal.classList.add('is-open');
      },
    });

    // Focus first input
    setTimeout(() => {
      const firstInput = this.modal.querySelector('input:not([type="hidden"])');
      if (firstInput) {
        firstInput.focus();
      }
    }, 300);
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    gsap.to(this.modal, {
      opacity: 0,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('hidden', '');
        document.body.style.overflow = '';

        // Restart Lenis smooth scroll
        const lenis = window.TRE?.getLenis?.();
        if (lenis) {
          lenis.start();
        }
      },
    });
  }

  async handleSubmit(e) {
    e.preventDefault();

    const submitBtn = this.form.querySelector('[type="submit"]');
    const originalText = submitBtn.textContent;

    // Disable button and show loading
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    // Gather form data
    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData.entries());

    try {
      // Simulate API call (replace with actual endpoint)
      await this.simulateSubmit(data);

      // Show success state
      this.showSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;

      // Show error message
      this.showError('Something went wrong. Please try again.');
    }
  }

  simulateSubmit(data) {
    // Simulate network request
    // Replace this with actual form submission logic
    return new Promise((resolve) => {
      console.log('Form submitted:', data);
      setTimeout(resolve, 1500);
    });
  }

  showSuccess() {
    const content = this.modal.querySelector('.inquiry-modal__content');
    if (!content) return;

    content.innerHTML = `
      <div class="inquiry-modal__success">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <h3>Thank You!</h3>
        <p>Your inquiry has been sent successfully. We'll get back to you within 24 hours.</p>
      </div>
    `;

    // Auto close after delay
    setTimeout(() => {
      this.close();
      // Reset form after closing
      setTimeout(() => {
        location.reload();
      }, 300);
    }, 3000);
  }

  showError(message) {
    // Create error element if it doesn't exist
    let errorEl = this.form.querySelector('.inquiry-modal__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'inquiry-modal__error';
      this.form.insertBefore(errorEl, this.form.firstChild);
    }

    errorEl.textContent = message;
    errorEl.style.display = 'block';

    // Auto-hide after 5 seconds
    setTimeout(() => {
      errorEl.style.display = 'none';
    }, 5000);
  }

  destroy() {
    // Cleanup if needed
  }
}
